/**
 * Lightweight session-scoped cache for semi-static reference tables.
 *
 * Pages like property-setup.vue refetch the same ~10 reference tables on
 * every property switch. These tables (spaces, equipment, rates, currencies,
 * tags, etc.) rarely change during a single session, so we cache them in
 * module-level state with a TTL and offer a single `getRef()` entry point.
 *
 * Usage:
 *   import { getRef, invalidateRef, invalidateAllRefs } from '@/services/refDataCache'
 *
 *   const spaces = await getRef('spaces', () => dbApi.get('/spaces?select=*&order=name'))
 *
 *   // After a successful save that mutated this table:
 *   invalidateRef('spaces')
 *
 *   // After a property switch (if you want everything fresh):
 *   invalidateAllRefs()
 *
 * Defaults:
 * - 5-minute TTL per entry
 * - Concurrent calls share a single in-flight Promise (no thundering-herd)
 * - Errors are NOT cached — a failed fetch lets the next call retry
 */

interface CacheEntry<T> {
  data: T | null
  fetchedAt: number
  inflight: Promise<T> | null
}

const TTL_MS = 5 * 60 * 1000
const cache = new Map<string, CacheEntry<any>>()

/**
 * Returns cached data if fresh, otherwise calls `loader()` once and caches the
 * result. Concurrent callers share the same in-flight promise so we never hit
 * the backend twice for the same key in parallel.
 */
export async function getRef<T>(key: string, loader: () => Promise<T>, ttlMs: number = TTL_MS): Promise<T> {
  const now = Date.now()
  const entry = cache.get(key)
  if (entry) {
    if (entry.inflight) return entry.inflight
    if (entry.data !== null && now - entry.fetchedAt < ttlMs) return entry.data as T
  }
  const inflight = (async () => {
    try {
      const data = await loader()
      cache.set(key, { data, fetchedAt: Date.now(), inflight: null })
      return data
    } catch (err) {
      // Don't poison the cache on failure — drop the in-flight promise so the
      // next call can retry.
      const e = cache.get(key)
      if (e) e.inflight = null
      throw err
    }
  })()
  cache.set(key, { data: entry?.data ?? null, fetchedAt: entry?.fetchedAt ?? 0, inflight })
  return inflight
}

/** Drop a specific key so the next getRef() call refetches. */
export function invalidateRef(key: string): void {
  cache.delete(key)
}

/**
 * Drop every key starting with `prefix`. Use for namespaced families of keys
 * (e.g. `events-list:<url>` variants per property/date-view) where a single
 * mutation should invalidate all variants, not just the current one.
 */
export function invalidateByPrefix(prefix: string): void {
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(prefix)) cache.delete(k)
  }
}

/** Drop everything (e.g., user logout, property switch). */
export function invalidateAllRefs(): void {
  cache.clear()
}

/**
 * Convenience wrapper for raw-fetch callsites (pages still using `fetch(url, { headers })`
 * instead of `dbApi`). Caches the parsed JSON body keyed by `key`. Returns
 * the JSON directly (NOT a Response object), so callers should treat it as
 * already-decoded data. Returns `null` when the response is not OK so callers
 * can preserve their existing `?? []` fallback patterns.
 */
export async function cachedFetchJson<T = any>(
  key: string,
  url: string,
  init: RequestInit = {},
  ttlMs?: number,
): Promise<T | null> {
  return getRef<T | null>(key, async () => {
    const res = await fetch(url, init)
    if (!res.ok) return null
    return res.json() as Promise<T>
  }, ttlMs)
}

/** Inspect cache state — useful in tests and debug panels. */
export function _refCacheSnapshot(): Record<string, { age_ms: number; has_data: boolean; inflight: boolean }> {
  const out: Record<string, { age_ms: number; has_data: boolean; inflight: boolean }> = {}
  const now = Date.now()
  cache.forEach((entry, key) => {
    out[key] = {
      age_ms: entry.fetchedAt ? now - entry.fetchedAt : -1,
      has_data: entry.data !== null,
      inflight: !!entry.inflight,
    }
  })
  return out
}
